// Terms of Service — legally binding copy, bundled into the app so it renders
// without a runtime fetch (a runtime fetch of /terms_of_service.md is swallowed
// by the Amplify SPA rewrite in production and renders index.html as text).
//
// English is the authoritative version. `es` is intentionally omitted until a
// reviewed Spanish translation is approved; the page falls back to `en`.
// Edit with care — see CLAUDE.md: have the owner review any legal-copy change.

const en = `# Terms of Service

_Last updated: July 30, 2026_
_Effective date: July 30, 2026_

## 1. Acceptance of Terms
By downloading, installing or using Nekochan (“App”) operated by Nekochan S.A. (“we,” “us,” “our”), you agree to be bound by these Terms of Service (“Terms”). If you don’t agree, please do not use the App.

## 2. Who We Are
**Entity:** Nekochan S.A., a company registered in Costa Rica.
**Address:** San José, Costa Rica
**Support:** help@nekochan.app

## 3. Your Account
- You must register to use premium features. We collect:
  - Email address
  - Usage activity and progress data
  - Any information Apple or Google provides via their sign-in APIs
- You must be at least 13 years old (or the minimum age of digital consent in your country, e.g. 16 in parts of the EU) to use the App. If you are a minor under local law, you must have your parent’s or guardian’s consent.
- You may log in via Apple, Google, or native email/password.

## 4. Subscriptions, Trials & Payments
- **Free tier:** The first 200 words of study are unlocked at no charge (other features may carry weekly limits).
- **Subscription plans:**
  - $9.99 per month
  - $99.99 per year
- **One-time license:** $300 (seasonal discounts may apply at our discretion)
- Purchases and refunds are handled by the store you bought from — **Apple’s App Store** for iOS or **Google Play** for Android — under that store’s terms. You may cancel at any time via your Apple ID or Google Play account settings; unused portions of a billing period will not be refunded except where required by law or store policy.

## 5. Permitted & Prohibited Uses
- **Permitted:** Use the App to learn Japanese vocabulary via mnemonics, SRS, anime voices, images, and the AI chat assistant.
- **Prohibited:**
  - Reverse-engineering, decompiling or tampering with the App
  - Copying, distributing or publicly sharing App content (e.g. image assets, example sentences, mnemonics) without our express permission
  - Any illegal or unauthorized use

## 6. Intellectual Property
- All UI, code, content, branding and artwork in the App are owned by Nekochan S.A.
- Third-party rights: We use the VoiceBox engine for TTS, AI-generated images, and Google’s Gemini models (accessed via the OpenRouter routing service) for the AI chat assistant — those components remain subject to their respective licenses and terms.

## 7. User-Generated Content & AI Chat
- The App does not allow you to upload or publicly post content.
- You may submit messages to the in-app AI chat assistant. You are responsible for what you submit; do not send unlawful content or other people's personal data. Chat messages are processed as described in our [Privacy Policy](/privacy-policy).

## 7A. AI Chat Assistant — Disclaimer
The AI chat assistant generates answers using an artificial-intelligence model (Google Gemini, accessed via OpenRouter). Responses are produced automatically and may be inaccurate, incomplete, or outdated. They are provided for language-learning assistance only and do not constitute professional advice. We are not liable for decisions made in reliance on AI-generated responses, to the maximum extent permitted by law.

## 8. Disclaimers & Limitation of Liability
- **“As-is”**: We make no guarantees regarding App performance, availability, or accuracy of learning content.
- **Liability cap**: To the maximum extent permitted by law, our total liability under these Terms is limited to the amount you paid us in the 12 months prior to any claim. Nothing in these Terms excludes or limits liability that cannot be excluded or limited under applicable law, including your mandatory consumer-protection rights.

## 9. Termination
We may suspend or terminate your access if you violate these Terms—especially for reverse-engineering or unauthorized redistribution. Upon termination, your account and progress data will be deleted upon request.

## 10. Privacy & Analytics
Your use of personal data is governed by our [Privacy Policy](/privacy-policy). We use Google Firebase (Firebase Analytics) and AWS for analytics, performance monitoring, and hosting, and additional analytics/advertising partners as described in the Privacy Policy.

## 11. Governing Law & Dispute Resolution
- **Jurisdiction:** Costa Rica
- **Disputes:** First, we encourage you to contact us to resolve any issue informally. If that fails, all disputes will be settled by binding arbitration in San José under the rules of the Costa Rica Chamber of Commerce, with each party bearing its own costs. Nothing in this section removes any mandatory consumer-protection rights you have under the laws of your country of residence, including any right to bring a claim before your local courts where such rights cannot be waived.

## 12. Changes to Terms
We may update these Terms from time to time. We’ll notify you by email at least 30 days before any material change. Your continued use after the effective date constitutes acceptance.

## 13. Apple App Store — Additional Terms
These additional terms apply if you download the App from the Apple App Store:
- These Terms are between you and Nekochan S.A. only, and not with Apple. Apple is not responsible for the App or its content.
- Apple has no obligation to provide maintenance or support for the App.
- In the event of any failure of the App to conform to any applicable warranty, you may notify Apple, and Apple will refund the purchase price (if any) for the App; to the maximum extent permitted by law, Apple has no other warranty obligation with respect to the App.
- Apple is not responsible for addressing any claims by you or a third party relating to the App or your use of it, including (i) product-liability claims, (ii) any claim that the App fails to conform to any applicable legal or regulatory requirement, and (iii) claims arising under consumer-protection or similar legislation.
- Apple is not responsible for investigating, defending, settling, or discharging any third-party intellectual-property infringement claim relating to the App.
- You represent that you are not located in a country subject to a U.S. Government embargo or designated as a “terrorist-supporting” country, and that you are not on any U.S. Government list of prohibited or restricted parties.
- **Apple and its subsidiaries are third-party beneficiaries of these Terms and, upon your acceptance, have the right to enforce these Terms against you.**

*(Google Play: the App is not affiliated with, sponsored, or endorsed by Google, and Google is not a party to these Terms.)*`;

const termsOfService = { en };

export default termsOfService;
